import { Core } from '@pdftron/webviewer';
import { Layer } from 'types';

interface LayerContext {
  visible: boolean;
  obj: string;
  name: string;
  locked: boolean;
  children: Core.Document.LayerContext[];
}

const maxDepth = 2; // max depth a Layer can have is a grandchild

export function convertWebviewerLayerToVerifyLayer(layer: LayerContext, depth: number = 0): Layer {
  if (depth <= maxDepth) {
    return {
      name: layer.name,
      visible: layer.visible,
      children: layer.children
        ? layer.children.map((child) => convertWebviewerLayerToVerifyLayer(child, depth + 1))
        : [],
    };
  }
  return {
    name: layer.name,
    visible: layer.visible,
  };
}

export function getUpdatedLayerContextForWebviewer(docLayers: LayerContext[], layers: Layer[]): LayerContext[] {
  return docLayers.map((docLayer) => {
    const matchingLayer = layers.find((layer) => layer.name === docLayer.name);
    if (!matchingLayer) return docLayer;

    return {
      ...docLayer,
      visible: matchingLayer.visible,
      children:
        docLayer.children && matchingLayer.children
          ? getUpdatedLayerContextForWebviewer(docLayer.children, matchingLayer.children)
          : docLayer.children,
    };
  });
}

/**
 * Builds the layer list shown in the panel. Prefers the authoritative list from the CTF
 * preprocessor (`preprocessorLayers`) when available, otherwise falls back to the layers
 * discovered in the document by PDFTron.
 *
 * Visibility precedence: saved session state (`sessionLayers`, e.g. a reopened inspection) wins,
 * then the auto-select `layersVisibility` recommendation, then the base list's own visibility.
 */
export function buildLayers(
  preprocessorLayers: Layer[] | undefined,
  documentLayers: Layer[],
  layersVisibility?: Layer[],
  sessionLayers?: Layer[],
): Layer[] {
  const base = preprocessorLayers && preprocessorLayers.length > 0 ? preprocessorLayers : documentLayers;
  const visibilityOverride = sessionLayers && sessionLayers.length > 0 ? sessionLayers : layersVisibility;
  return applyLayersVisibilityFromMetadata(base, visibilityOverride);
}

export function applyLayersVisibilityFromMetadata(layers: Layer[], metadataLayers?: Layer[]): Layer[] {
  if (!metadataLayers || metadataLayers.length === 0) return layers;

  const apply = (documentLayers: Layer[], metadataLayersLevel: Layer[]): Layer[] => {
    const metaByName = new Map<string, Layer>(metadataLayersLevel.map((layer) => [layer.name, layer]));
    return documentLayers.map((layer) => {
      const metaLayer = metaByName.get(layer.name);
      const updatedChildren = Array.isArray(layer.children)
        ? apply(layer.children, Array.isArray(metaLayer?.children) ? metaLayer.children : [])
        : undefined;

      return {
        ...layer,
        visible: typeof metaLayer?.visible === 'boolean' ? metaLayer.visible : layer.visible,
        ...(updatedChildren !== undefined && { children: updatedChildren }),
      };
    });
  };

  return apply(layers, metadataLayers);
}
